import {useMemo, useState} from 'react';

import {Illustration} from 'shells/illustrations/illustration';
import {TwoPaneModal} from 'shells/modal/two-pane-modal';

import {useGetAvailablePlans} from '../../../setup/billing/graphql/hooks/use-get-available-plans';

import {QuotesPromoConfirmStep} from './quotes-promo-confirm-step';
import {QuotesPromoOfferStep} from './quotes-promo-offer-step';
import {QuotesPromoSuccessStep} from './quotes-promo-success-step';

import styles from './quotes-promo-modal.module.css';

type Step = 'offer' | 'confirm' | 'success';

interface Props {
    onClose: () => void;
    onActivated: () => void;
}

export function QuotesPromoModal(props: Props) {
    const {onClose, onActivated} = props;

    const [step, setStep] = useState<Step>('offer');
    const [isActivating, setIsActivating] = useState<boolean>(false);

    const {addOns, crm, engagement, marketingSuite} = useGetAvailablePlans();

    const quotesAddOn = addOns?.quotes;

    const ModalStep = useMemo(() => {
        switch (step) {
            case 'offer':
                return (
                    <QuotesPromoOfferStep
                        quotesAddOn={quotesAddOn}
                        onClose={onClose}
                        onContinue={() => setStep('confirm')}
                    />
                );
            case 'confirm':
                return (
                    <QuotesPromoConfirmStep
                        onClose={onClose}
                        onSuccess={() => {
                            onActivated();
                            setStep('success');
                        }}
                        setIsActivating={setIsActivating}
                        quotesAddOn={quotesAddOn}
                        crm={crm}
                        engagement={engagement}
                        marketingSuite={marketingSuite}
                        addOns={addOns}
                    />
                );
            case 'success':
                return <QuotesPromoSuccessStep onClose={onClose} />;
            default:
                return null;
        }
    }, [
        step,
        onClose,
        onActivated,
        setIsActivating,
        quotesAddOn,
        crm,
        engagement,
        marketingSuite,
        addOns,
    ]);

    const illustrationSrc = useMemo(() => {
        if (step === 'success') {
            return 'nutshell-grow';
        }

        return 'quote-and-invoice';
    }, [step]);

    return (
        <TwoPaneModal
            // @ts-expect-error
            isOpen={true}
            onClose={onClose}
            maxHeight={700}
            closeButton={false}
            canClickOutsideToClose={!isActivating}
            canEscapeToClose={!isActivating}
            leftSection={
                <div className={styles['left-section']}>
                    <Illustration src={illustrationSrc} width={280} />
                </div>
            }
            rightSection={ModalStep}
        />
    );
}
